import {useEffect} from 'react';

import {createSDK, toolInstanceObj, isAPIName, isAsyncAPI, isEventAPI, isRequestType} from '@/kuTools';
import {EventKeys} from '@/kuTools/eventBus/bus';
import {msgIdPrefix, API_RESPONSE_TYPE, EVENT_RESPONSE_TYPE, ErrorCode} from '@/kuTools/constants';
import {getKnowledgePageRouteMatchInfo} from '@/utils/urlUtils';
import {getIsSdkPage} from '@/kuSdk/view/DocViewAll/utils/isSdkPage';

export function useApiMessageHandler() {
    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            const {type, name, id, appId, data} = event.data;

            if (
                isRequestType(type) &&
                typeof id === 'string' &&
                id.startsWith(msgIdPrefix) &&
                isAPIName(name)
            ) {
                const toolInstance = toolInstanceObj.getVal(appId);
                const ToolSDK = toolInstance ? toolInstance : createSDK({appId});

                if (!toolInstance) {
                    toolInstanceObj.setVal(appId, ToolSDK);
                }

                const [scope, fn] = name.split('.');
                const isEvent = isEventAPI(name, type);

                if (!ToolSDK[scope]?.[fn] && !isEvent) {
                    return;
                }

                const sendApiSuccessResponse = (res: any) => {
                    event.source?.postMessage({
                        id,
                        type: API_RESPONSE_TYPE,
                        body: {
                            code: 200,
                            message: 'success',
                            data: res,
                        },
                    }, {targetOrigin: event.origin});
                };

                const sendApiErrorResponse = ({code = 500, message = '未知错误'}) => {
                    event.source?.postMessage({
                        id,
                        type: API_RESPONSE_TYPE,
                        body: {
                            code,
                            message,
                        },
                    }, {targetOrigin: event.origin});
                };

                const sendEventResponse = (res: any) => {
                    event.source?.postMessage({
                        id,
                        type: EVENT_RESPONSE_TYPE,
                        body: {
                            code: 200,
                            message: 'success',
                            data: res,
                        },
                    }, {targetOrigin: event.origin});
                };

                const {isDocPage} = getIsSdkPage() ? {isDocPage: true} : getKnowledgePageRouteMatchInfo();

                // 非文档页暂不支持
                if (!isDocPage) {
                    sendApiErrorResponse({code: ErrorCode.CanNotUse, message: 'api 不可用'});
                }

                if (isEvent) {
                    switch (fn) {
                        case 'onDocSave':
                            ToolSDK.event.on(EventKeys.DOC_SAVE, (data) => {
                                sendEventResponse(data);
                            });
                            break;
                        default:
                            break;
                    }

                    return;
                }

                if (isAsyncAPI(name)) {
                    ToolSDK[scope][fn](data)
                        .then(sendApiSuccessResponse)
                        .catch((error: any) => {
                            console.error(error);
                            sendApiErrorResponse({code: 500, message: 'api 调用失败'});
                        });
                } else {
                    try {
                        const res = ToolSDK[scope][fn](data);
                        sendApiSuccessResponse(res);
                    } catch (error) {
                        console.error(error);
                        sendApiErrorResponse({code: 500, message: 'api 调用失败'});
                    }
                }
            }
        };

        window.addEventListener('message', handleMessage);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);
}
